import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Empty, message } from "antd";
import PreviewLanguage from "../../Preview/PreviewComponents/PreviewLanguage";
import { useSurveyInputStore } from "../../../surveyStore";
import { orderedItems } from "../../../helpers";
import SurveyStep from "./SurveyStep";
import TetoriRozeActions from "./TetoriRozeActions";
import { buildSteps, getProgress, getVisibleSteps, resolveActiveKey } from "./steps";
import { content } from "./content";

const LANGUAGE_LABELS = {
   language: content.card.languageLabel,
   print: content.card.printLabel,
   languageNames: content.card.languageNames
};

/**
 * Replaces Preview's card (language bar + SurveyDataItems + PreviewActions) for this survey with
 * a one-section-at-a-time card. The questions are still the platform's own Section/ItemSelector
 * components; submitting, drafts and printing still run through Preview's handlers.
 */
const TetoriRozeSurvey = ({
   printRef,
   surveyAlldata,
   surveyData,
   surveyFetchError,
   hasErrors,
   setHasErrors,
   submissionId,
   isArabic,
   showSurveyActions,
   actionProps
}) => {
   const cardRef = useRef(null);
   const stepElements = useRef({});

   const steps = useMemo(
      () =>
         buildSteps(orderedItems(surveyData ?? []), {
            introSectionName: content.introSectionName
         }),
      [surveyData]
   );

   const [hiddenByKey, setHiddenByKey] = useState({});
   const [requestedKey, setRequestedKey] = useState(null);
   const [validateWhenShown, setValidateWhenShown] = useState(null);

   const visibleSteps = useMemo(() => getVisibleSteps(steps, hiddenByKey), [steps, hiddenByKey]);
   const activeKey = resolveActiveKey(steps, visibleSteps, requestedKey ?? visibleSteps[0]?.key);
   const activeIndex = visibleSteps.findIndex((step) => step.key === activeKey);

   // Reviewing a submitted response shows the whole survey at once.
   const showAllSteps = Boolean(submissionId);

   const handleVisibilityChange = useCallback((key, isVisible) => {
      setHiddenByKey((previous) =>
         Boolean(previous[key]) === !isVisible ? previous : { ...previous, [key]: !isVisible }
      );
   }, []);

   const getStepProblems = useCallback((key) => {
      const element = stepElements.current[key];
      if (!element) {
         return { firstRequired: null, constraint: null };
      }

      const rendered = [...element.querySelectorAll("[data-id]")];
      const { inputs, getErrors } = useSurveyInputStore.getState();
      const missing = getErrors(
         rendered.map((node) => node.getAttribute("data-id")),
         inputs
      );

      return {
         firstRequired: rendered.find((node) => missing.includes(node.getAttribute("data-id"))),
         constraint: element.querySelector(".preview-question-constraint")
      };
   }, []);

   // Same checks Preview's submit makes over the whole form, limited to one step.
   const validateStep = useCallback(
      (key) => {
         const { firstRequired, constraint } = getStepProblems(key);
         const problem = firstRequired ?? constraint;

         if (!problem) {
            return true;
         }

         setHasErrors(true);
         problem.scrollIntoView({ behavior: "smooth", block: "center" });
         message.open({
            type: "error",
            content: firstRequired
               ? content.actions.requiredMissing
               : content.actions.constraintFailed
         });
         return false;
      },
      [getStepProblems, setHasErrors]
   );

   const goTo = (key) => {
      setRequestedKey(key);
   };

   const handleNext = () => {
      if (!validateStep(activeKey)) {
         return;
      }
      setHasErrors(false);
      goTo(visibleSteps[activeIndex + 1]?.key);
   };

   const handleBack = () => {
      setHasErrors(false);
      goTo(visibleSteps[activeIndex - 1]?.key);
   };

   const handleFinish = () => {
      // A loaded draft can carry gaps in steps the respondent never revisited: show the first one.
      const stepWithProblem = visibleSteps.find((step) => {
         const { firstRequired, constraint } = getStepProblems(step.key);
         return firstRequired || constraint;
      });

      if (stepWithProblem) {
         goTo(stepWithProblem.key);
         setValidateWhenShown(stepWithProblem.key);
         return;
      }

      actionProps.submitProps.onClick();
   };

   // Saving a draft or starting a new one empties the form: start again from the first step.
   const hasAnswers = useSurveyInputStore((state) => Object.keys(state.inputs ?? {}).length > 0);
   const hadAnswers = useRef(hasAnswers);
   useEffect(() => {
      if (hadAnswers.current && !hasAnswers) {
         setRequestedKey(null);
      }
      hadAnswers.current = hasAnswers;
   }, [hasAnswers]);

   // On a step change, bring the card into view and move focus to the new section's heading.
   const previousActiveKey = useRef(activeKey);
   useEffect(() => {
      if (previousActiveKey.current !== activeKey && previousActiveKey.current != null) {
         const card = cardRef.current;
         if (card && card.getBoundingClientRect().top < 0) {
            card.scrollIntoView({ behavior: "smooth", block: "start" });
         }
         stepElements.current[activeKey]
            ?.querySelector("h2[tabindex]")
            ?.focus({ preventScroll: true });
      }
      previousActiveKey.current = activeKey;
   }, [activeKey]);

   useEffect(() => {
      if (validateWhenShown && validateWhenShown === activeKey) {
         validateStep(activeKey);
         setValidateWhenShown(null);
      }
   }, [validateWhenShown, activeKey, validateStep]);

   const hasSurvey = surveyData?.length > 0;

   return (
      <div ref={cardRef} className={`tr-card${isArabic ? " arabic-survey" : ""}`}>
         <PreviewLanguage ref={printRef} surveyAlldata={surveyAlldata} labels={LANGUAGE_LABELS} />

         {hasSurvey ? (
            <div className={`tr-steps${showAllSteps ? " tr-steps--all" : ""}`}>
               {steps.map((step) => (
                  <SurveyStep
                     key={step.key}
                     step={step}
                     isActive={showAllSteps || step.key === activeKey}
                     progress={getProgress(visibleSteps, step.key)}
                     hasErrors={hasErrors}
                     submissionId={submissionId}
                     onVisibilityChange={handleVisibilityChange}
                     stepRef={(element) => {
                        stepElements.current[step.key] = element;
                     }}
                  />
               ))}
            </div>
         ) : surveyFetchError ? (
            <Alert
               type="error"
               showIcon
               style={{ margin: "50px 24px" }}
               message="Failed to load the survey"
               description={surveyFetchError}
            />
         ) : (
            <Empty style={{ padding: "50px 0" }} />
         )}

         {hasSurvey && !showAllSteps && (
            <TetoriRozeActions
               canGoBack={activeIndex > 0}
               isLastStep={activeIndex === visibleSteps.length - 1}
               onBack={handleBack}
               onNext={handleNext}
               onFinish={handleFinish}
               showSurveyActions={showSurveyActions}
               {...actionProps}
            />
         )}
      </div>
   );
};

export default TetoriRozeSurvey;
